import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Calendar, Trophy, Users, Clock, Plus, X, Loader2, ChevronRight, Upload, Github, Video, Globe, Award } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HackathonsPage() {
  const { t } = useLanguage();
  const [hackathons, setHackathons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHackathon, setSelectedHackathon] = useState(null);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [submissionOpen, setSubmissionOpen] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState({});
  const [activeTab, setActiveTab] = useState('info');

  // Registration form
  const [formData, setFormData] = useState({
    teamName: '',
    teamMembers: [{ name: '', role: '', github: '' }],
    track: '',
    projectIdea: '',
    contactEmail: ''
  });

  // Submission form
  const [submissionData, setSubmissionData] = useState({
    registrationId: '',
    projectName: '',
    description: '',
    demoUrl: '',
    repoUrl: '',
    videoUrl: '',
    presentationUrl: '',
    techStack: ''
  });

  useEffect(() => {
    fetchHackathons();
  }, []);

  const fetchHackathons = async () => {
    try {
      const res = await axios.get(`${API}/hackathons`);
      setHackathons(res.data);
    } catch (error) {
      console.error('Error fetching hackathons:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async (hackathonId) => {
    try {
      const res = await axios.get(`${API}/hackathons/${hackathonId}/submissions`);
      setSubmissions(prev => ({ ...prev, [hackathonId]: res.data.submissions }));
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const handleAddMember = () => {
    setFormData(prev => ({
      ...prev,
      teamMembers: [...prev.teamMembers, { name: '', role: '', github: '' }]
    }));
  };

  const handleRemoveMember = (index) => {
    if (formData.teamMembers.length > 1) {
      setFormData(prev => ({
        ...prev,
        teamMembers: prev.teamMembers.filter((_, i) => i !== index)
      }));
    }
  };

  const handleMemberChange = (index, field, value) => {
    const newMembers = [...formData.teamMembers];
    newMembers[index][field] = value;
    setFormData(prev => ({ ...prev, teamMembers: newMembers }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!selectedHackathon || !formData.teamName || !formData.track || !formData.contactEmail) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsRegistering(true);
    try {
      await axios.post(`${API}/hackathons/${selectedHackathon.id}/register`, {
        hackathon_id: selectedHackathon.id,
        team_name: formData.teamName,
        team_members: formData.teamMembers,
        track: formData.track,
        project_idea: formData.projectIdea,
        contact_email: formData.contactEmail
      });

      toast.success(t('hackathons.registrationSuccess'));
      setRegistrationOpen(false);
      setFormData({
        teamName: '',
        teamMembers: [{ name: '', role: '', github: '' }],
        track: '',
        projectIdea: '',
        contactEmail: ''
      });
      fetchHackathons();
    } catch (error) {
      const message = error.response?.data?.detail || t('hackathons.registrationError');
      toast.error(message);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleSubmitProject = async (e) => {
    e.preventDefault();
    if (!submissionData.registrationId || !submissionData.projectName || !submissionData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/hackathons/${selectedHackathon.id}/submit`, {
        registration_id: submissionData.registrationId,
        project_name: submissionData.projectName,
        description: submissionData.description,
        demo_url: submissionData.demoUrl || null,
        repo_url: submissionData.repoUrl || null,
        video_url: submissionData.videoUrl || null,
        presentation_url: submissionData.presentationUrl || null,
        tech_stack: submissionData.techStack
      });

      toast.success('Project submitted successfully!');
      setSubmissionOpen(false);
      setSubmissionData({
        registrationId: '',
        projectName: '',
        description: '',
        demoUrl: '',
        repoUrl: '',
        videoUrl: '',
        presentationUrl: '',
        techStack: ''
      });
      fetchSubmissions(selectedHackathon.id);
    } catch (error) {
      const message = error.response?.data?.detail || 'Error submitting project';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      upcoming: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      registration_open: 'bg-green-500/20 text-green-400 border-green-500/30',
      active: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      judging: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      completed: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
    };
    return styles[status] || styles.upcoming;
  };

  const getStatusLabel = (status) => {
    const labels = {
      upcoming: t('hackathons.statusUpcoming'),
      registration_open: t('hackathons.statusOpen'),
      active: t('hackathons.statusActive'),
      judging: t('hackathons.statusJudging'),
      completed: t('hackathons.statusCompleted')
    };
    return labels[status] || status;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#02040A]" data-testid="hackathons-page">
      <Navbar />
      
      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="hackathons-headline">
              {t('hackathons.title')}
            </h1>
            <p className="text-slate-300 max-w-2xl mx-auto">
              {t('hackathons.subtitle')}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : hackathons.length === 0 ? (
            <div className="glass-card rounded-xl p-12 text-center">
              <Trophy className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400">No hackathons available at the moment. Check back soon!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {hackathons.map((hackathon) => (
                <div 
                  key={hackathon.id} 
                  className="glass-card rounded-xl overflow-hidden"
                  data-testid={`hackathon-${hackathon.id}`}
                >
                  <div className="p-6 md:p-8">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                      {/* Left - Info */}
                      <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-3">
                          <h2 className="text-xl md:text-2xl font-bold text-white">{hackathon.title}</h2>
                          <Badge className={getStatusBadge(hackathon.status)}>
                            {getStatusLabel(hackathon.status)}
                          </Badge>
                        </div>
                        
                        <p className="text-slate-300 mb-4">{hackathon.description}</p>

                        {/* Meta info */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span className="text-slate-400">{t('hackathons.startDate')}:</span>
                            <span className="text-white">{formatDate(hackathon.start_date)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span className="text-slate-400">{t('hackathons.endDate')}:</span>
                            <span className="text-white">{formatDate(hackathon.end_date)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-yellow-400" />
                            <span className="text-slate-400">{t('hackathons.deadline')}:</span>
                            <span className="text-white">{formatDate(hackathon.registration_deadline)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Users className="w-4 h-4 text-green-400" />
                            <span className="text-slate-400">{t('hackathons.participants')}:</span>
                            <span className="text-white">{hackathon.current_participants}/{hackathon.max_participants}</span>
                          </div>
                        </div>

                        {/* Tracks */}
                        <div className="mb-4">
                          <span className="text-sm text-slate-400 mr-2">{t('hackathons.tracks')}:</span>
                          <div className="inline-flex flex-wrap gap-2">
                            {hackathon.tracks.map((track) => (
                              <Badge key={track} variant="outline" className="text-xs">
                                {track}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {/* Prizes */}
                        <div>
                          <span className="text-sm text-slate-400 mr-2">{t('hackathons.prizes')}:</span>
                          <div className="inline-flex flex-wrap gap-3 mt-2">
                            {hackathon.prizes.slice(0, 3).map((prize, i) => (
                              <div key={i} className="flex items-center gap-1 text-sm">
                                <Trophy className={`w-4 h-4 ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-slate-300' : 'text-orange-400'}`} />
                                <span className="text-white font-medium">{prize.amount}</span>
                                <span className="text-slate-400">- {prize.place}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Right - CTAs */}
                      <div className="flex flex-col gap-3">
                        {['upcoming', 'registration_open'].includes(hackathon.status) && (
                          <Button 
                            className="glow-primary group"
                            onClick={() => {
                              setSelectedHackathon(hackathon);
                              setRegistrationOpen(true);
                            }}
                            data-testid={`register-btn-${hackathon.id}`}
                          >
                            {t('hackathons.registerNow')}
                            <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        )}
                        
                        {['registration_open', 'active'].includes(hackathon.status) && (
                          <Button 
                            variant="outline"
                            onClick={() => {
                              setSelectedHackathon(hackathon);
                              setSubmissionOpen(true);
                              fetchSubmissions(hackathon.id);
                            }}
                            data-testid={`submit-btn-${hackathon.id}`}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Submit Project
                          </Button>
                        )}
                        
                        {submissions[hackathon.id]?.length > 0 && (
                          <Button 
                            variant="ghost"
                            onClick={() => {
                              setSelectedHackathon(hackathon);
                              setActiveTab('submissions');
                              setSubmissionOpen(true);
                            }}
                          >
                            <Award className="w-4 h-4 mr-2" />
                            View Submissions ({submissions[hackathon.id]?.length})
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Registration Dialog */}
      <Dialog open={registrationOpen} onOpenChange={setRegistrationOpen}>
        <DialogContent className="bg-[#0B0F19] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{t('hackathons.registration')}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedHackathon?.title}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleRegister} className="space-y-6 mt-4">
            {/* Team Name */}
            <div className="space-y-2">
              <Label className="text-slate-300">{t('hackathons.teamName')} *</Label>
              <Input
                value={formData.teamName}
                onChange={(e) => setFormData(prev => ({ ...prev, teamName: e.target.value }))}
                placeholder="Enter your team name"
                className="bg-white/5 border-white/10 text-white"
                required
                data-testid="hackathon-team-name"
              />
            </div>

            {/* Team Members */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">{t('hackathons.teamMembers')}</Label>
                <Button type="button" variant="ghost" size="sm" onClick={handleAddMember}>
                  <Plus className="w-4 h-4 mr-1" /> {t('hackathons.addMember')}
                </Button>
              </div>
              
              {formData.teamMembers.map((member, index) => (
                <div key={index} className="glass-card rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Member {index + 1}</span>
                    {formData.teamMembers.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveMember(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      placeholder={t('hackathons.memberName')}
                      value={member.name}
                      onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                    />
                    <Input
                      placeholder={t('hackathons.memberRole')}
                      value={member.role}
                      onChange={(e) => handleMemberChange(index, 'role', e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                    />
                    <Input
                      placeholder={t('hackathons.memberGithub')}
                      value={member.github}
                      onChange={(e) => handleMemberChange(index, 'github', e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Track Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300">{t('hackathons.selectTrack')} *</Label>
              <Select 
                value={formData.track} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, track: value }))}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="hackathon-track">
                  <SelectValue placeholder={t('hackathons.selectTrack')} />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0F19] border-white/10">
                  {selectedHackathon?.tracks.map((track) => (
                    <SelectItem key={track} value={track} className="text-white hover:bg-white/10">
                      {track}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Project Idea */}
            <div className="space-y-2">
              <Label className="text-slate-300">{t('hackathons.projectIdea')}</Label>
              <Textarea
                value={formData.projectIdea}
                onChange={(e) => setFormData(prev => ({ ...prev, projectIdea: e.target.value }))}
                placeholder="Briefly describe your project idea..."
                className="bg-white/5 border-white/10 text-white min-h-[100px]"
                data-testid="hackathon-project-idea"
              />
            </div>

            {/* Contact Email */}
            <div className="space-y-2">
              <Label className="text-slate-300">{t('hackathons.contactEmail')} *</Label>
              <Input
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                placeholder="team@example.com"
                className="bg-white/5 border-white/10 text-white"
                required
                data-testid="hackathon-email"
              />
            </div>

            {/* Submit */}
            <Button 
              type="submit" 
              className="w-full glow-primary" 
              disabled={isRegistering}
              data-testid="hackathon-submit"
            >
              {isRegistering ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Registering...
                </>
              ) : (
                t('hackathons.registerTeam')
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Submission Dialog */}
      <Dialog open={submissionOpen} onOpenChange={setSubmissionOpen}>
        <DialogContent className="bg-[#0B0F19] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Project Submission</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedHackathon?.title}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid w-full grid-cols-2 bg-white/5">
              <TabsTrigger value="submit">Submit Project</TabsTrigger>
              <TabsTrigger value="submissions">Submissions ({submissions[selectedHackathon?.id]?.length || 0})</TabsTrigger>
            </TabsList>

            <TabsContent value="submit" className="space-y-4 mt-4">
              <form onSubmit={handleSubmitProject} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Registration ID *</Label>
                  <Input
                    value={submissionData.registrationId}
                    onChange={(e) => setSubmissionData(prev => ({ ...prev, registrationId: e.target.value }))}
                    placeholder="Your registration ID from confirmation email"
                    className="bg-white/5 border-white/10 text-white"
                    required
                    data-testid="submission-registration-id"
                  />
                  <p className="text-xs text-slate-500">Enter the registration ID you received when registering</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Project Name *</Label>
                  <Input
                    value={submissionData.projectName}
                    onChange={(e) => setSubmissionData(prev => ({ ...prev, projectName: e.target.value }))}
                    placeholder="Your project name"
                    className="bg-white/5 border-white/10 text-white"
                    required
                    data-testid="submission-project-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Description *</Label>
                  <Textarea
                    value={submissionData.description}
                    onChange={(e) => setSubmissionData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe your project..."
                    className="bg-white/5 border-white/10 text-white min-h-[100px]"
                    required
                    data-testid="submission-description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300 flex items-center gap-2">
                      <Globe className="w-4 h-4" /> Demo URL
                    </Label>
                    <Input
                      value={submissionData.demoUrl}
                      onChange={(e) => setSubmissionData(prev => ({ ...prev, demoUrl: e.target.value }))}
                      placeholder="https://..."
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300 flex items-center gap-2">
                      <Github className="w-4 h-4" /> Repo URL
                    </Label>
                    <Input
                      value={submissionData.repoUrl}
                      onChange={(e) => setSubmissionData(prev => ({ ...prev, repoUrl: e.target.value }))}
                      placeholder="https://github.com/..."
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300 flex items-center gap-2">
                      <Video className="w-4 h-4" /> Video URL
                    </Label>
                    <Input
                      value={submissionData.videoUrl}
                      onChange={(e) => setSubmissionData(prev => ({ ...prev, videoUrl: e.target.value }))}
                      placeholder="YouTube/Loom link"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">Presentation URL</Label>
                    <Input
                      value={submissionData.presentationUrl}
                      onChange={(e) => setSubmissionData(prev => ({ ...prev, presentationUrl: e.target.value }))}
                      placeholder="Slides link"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Tech Stack *</Label>
                  <Input
                    value={submissionData.techStack}
                    onChange={(e) => setSubmissionData(prev => ({ ...prev, techStack: e.target.value }))}
                    placeholder="React, Solidity, Node.js..."
                    className="bg-white/5 border-white/10 text-white"
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full glow-primary" 
                  disabled={isSubmitting}
                  data-testid="submit-project-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Project'
                  )}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="submissions" className="mt-4">
              {submissions[selectedHackathon?.id]?.length > 0 ? (
                <div className="space-y-3">
                  {submissions[selectedHackathon?.id].map((sub) => (
                    <div key={sub.id} className="glass-card rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-white">{sub.project_name}</h4>
                          <p className="text-sm text-slate-400">by {sub.team_name}</p>
                          <p className="text-sm text-slate-300 mt-2">{sub.description}</p>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline">{sub.track}</Badge>
                            {sub.score !== null && (
                              <Badge className="bg-green-500/20 text-green-400">
                                Score: {sub.score}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {sub.demo_url && (
                            <a href={sub.demo_url} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="sm"><Globe className="w-4 h-4" /></Button>
                            </a>
                          )}
                          {sub.repo_url && (
                            <a href={sub.repo_url} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="sm"><Github className="w-4 h-4" /></Button>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  No submissions yet for this hackathon
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
